import type { TAdminUser } from "@/api/admin/admin.types";
import { verificationAdminService } from "@/api/admin/verification-admin.service";
import type {
  TConfigField,
  TVerificationConfigResponse,
  TVerificationConfigUpdate,
  TVerificationStatsResponse,
} from "@/api/admin/verification-admin.types";
import { UserPicker } from "@/components/admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Skeleton } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/utils";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Lock,
  Mail,
  MessageSquare,
  RefreshCw,
  RotateCcw,
  Save,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";

// Stat Cards
function StatCard({
  title,
  value,
  sub,
  color,
  icon: Icon,
}: {
  title: string;
  value: string;
  sub: string;
  color: string;
  icon?: React.ElementType;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          {Icon && (
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <Icon className="h-4 w-4" />
            </span>
          )}
        </div>
        <p className={`mt-2 text-2xl font-bold ${color}`}>{value}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>
      </CardContent>
    </Card>
  );
}

// Config Field Row
type TEdits = Record<string, string>;

function FieldRow({
  field,
  edits,
  onChange,
}: {
  field: TConfigField;
  edits: TEdits;
  onChange: (key: string, val: string) => void;
}) {
  const isDirty = field.key in edits && edits[field.key] !== String(field.value);
  const currentVal = field.key in edits ? edits[field.key] : String(field.value);

  return (
    <div className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:gap-4">
      {/* Key label */}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <code className="break-all rounded bg-muted px-1.5 py-0.5 text-xs font-mono text-foreground">
            {field.key}
          </code>
          {!field.editable && (
            <span className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-400">
              <Lock className="h-2.5 w-2.5" />
              Security-sensitive — read-only
            </span>
          )}
        </div>
        {field.description ? (
          <p className="mt-1 text-xs text-muted-foreground">{field.description}</p>
        ) : null}
      </div>

      {/* Value input / display */}
      <div className="relative w-full sm:w-40">
        {field.editable ? (
          <Input
            value={currentVal}
            onChange={(e) => onChange(field.key, e.target.value)}
            className={isDirty ? "border-primary pr-6" : ""}
            type={typeof field.value === "boolean" ? "text" : "number"}
            min={0}
          />
        ) : (
          <div className="flex h-9 items-center rounded-md border border-border bg-muted px-3 text-sm text-muted-foreground select-none">
            {String(field.value)}
          </div>
        )}
        {isDirty && field.editable && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-primary">
            *
          </span>
        )}
      </div>
    </div>
  );
}

// Config Section
function ConfigSection({
  title,
  description,
  icon: Icon,
  fields,
  edits,
  onChange,
  columns = 1,
  className,
}: {
  title: string;
  description: string;
  icon: React.ElementType;
  fields: TConfigField[];
  edits: TEdits;
  onChange: (key: string, val: string) => void;
  /** 1 = single-column stacked list; 2 = two-column field grid. */
  columns?: 1 | 2;
  className?: string;
}) {
  if (!fields?.length) return null;
  const editableCount = fields.filter((f) => f.editable).length;

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-primary" />
          <CardTitle>{title}</CardTitle>
        </div>
        <CardDescription>
          {description}{" "}
          <span className="text-muted-foreground">
            ({editableCount} editable, {fields.length - editableCount} read-only)
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div
          className={
            columns === 2
              ? "grid grid-cols-1 gap-x-8 sm:grid-cols-2"
              : "divide-y divide-border"
          }
        >
          {fields.map((f) => (
            <FieldRow
              key={f.key}
              field={f}
              edits={edits}
              onChange={onChange}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Page
export function VerificationSettingsPage() {
  const { toast } = useToast();

  // Stats
  const [stats, setStats] = useState<TVerificationStatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  // Config
  const [config, setConfig] = useState<TVerificationConfigResponse | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);
  const [edits, setEdits] = useState<TEdits>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Reset daily limit
  const [resetUser, setResetUser] = useState<TAdminUser | null>(null);
  const [resetChannel, setResetChannel] = useState<"email" | "phone">("email");
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // Loaders
  const loadStats = useCallback(async (signal?: AbortSignal) => {
    setStatsLoading(true);
    setStatsError(null);
    const { data, error: err } = await verificationAdminService.getStats({
      signal,
    });
    if (signal?.aborted) return;
    if (err) {
      const msg = await extractErrorMessage(err);
      setStatsError(msg);
    } else if (data) {
      setStats(data.data ?? null);
    }
    setStatsLoading(false);
  }, []);

  const loadConfig = useCallback(async (signal?: AbortSignal) => {
    setConfigLoading(true);
    setConfigError(null);
    const { data, error: err } = await verificationAdminService.getConfig({
      signal,
    });
    if (signal?.aborted) return;
    if (err) {
      const msg = await extractErrorMessage(err);
      setConfigError(msg);
    } else if (data) {
      setConfig(data.data ?? null);
    }
    setEdits({});
    setConfigLoading(false);
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void loadStats(ctrl.signal);
    void loadConfig(ctrl.signal);
    return () => ctrl.abort();
  }, [loadStats, loadConfig]);

  // Save
  const handleSave = async () => {
    if (!config || Object.keys(edits).length === 0) return;

    const payload: TVerificationConfigUpdate = {};
    for (const [key, val] of Object.entries(edits)) {
      (payload as Record<string, number | boolean | string>)[key] = isNaN(
        Number(val)
      )
        ? val
        : Number(val);
    }

    setIsSaving(true);
    setSaveSuccess(false);
    const { data, error: err } = await verificationAdminService.updateConfig(
      payload
    );
    setIsSaving(false);

    if (err) {
      const msg = await extractErrorMessage(err);
      toast({ variant: "destructive", title: "Failed to save", description: msg });
    } else if (data) {
      setConfig(data.data ?? null);
      setEdits({});
      setSaveSuccess(true);
      toast({ title: "Configuration saved successfully" });
      setTimeout(() => setSaveSuccess(false), 3000);
    }
  };

  const handleFieldChange = (key: string, val: string) => {
    setEdits((e) => ({ ...e, [key]: val }));
    setSaveSuccess(false);
  };

  const handleResetDailyLimit = async () => {
    if (!resetUser) return;
    setIsResetting(true);
    const { data, error: err } = await verificationAdminService.resetDailyLimit(
      resetUser.id,
      resetChannel
    );
    setIsResetting(false);
    setResetConfirmOpen(false);

    if (err) {
      const msg = await extractErrorMessage(err);
      toast({ variant: "destructive", title: "Failed to reset limit", description: msg });
    } else {
      toast({
        title: "Daily limit reset",
        description: data?.data?.message ?? `Reset ${resetChannel} limit for ${resetUser.email}.`,
      });
      setResetUser(null);
    }
  };

  const dirtyCount = Object.keys(edits).filter((k) => {
    const allFields = [
      ...(config?.email ?? []),
      ...(config?.phone ?? []),
      ...(config?.shared ?? []),
    ];
    const original = allFields.find((f) => f.key === k);
    return original && edits[k] !== String(original.value);
  }).length;

  // Stats cards
  const emailDeliveryRate = stats
    ? `${(stats.email.delivery_rate * 100).toFixed(1)}%`
    : "—";
  const phoneDeliveryRate = stats
    ? `${(stats.phone.delivery_rate * 100).toFixed(1)}%`
    : "—";
  const emailTotal = stats ? String(stats.email.total) : "—";
  const phoneTotal = stats ? String(stats.phone.total) : "—";

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="hidden h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary sm:flex">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground sm:text-3xl">
                Verification Settings
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Live delivery stats and runtime configuration for OTP verification
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void loadStats();
                void loadConfig();
              }}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Delivery Statistics
          </h2>
          {statsLoading ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-24 rounded-xl" />
              ))}
            </div>
          ) : statsError ? (
            <div className="flex items-center gap-2 rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {statsError}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                title="Email Total"
                value={emailTotal}
                sub={`${stats?.email.sent ?? 0} sent`}
                color="text-foreground"
                icon={Mail}
              />
              <StatCard
                title="Email Delivery"
                value={emailDeliveryRate}
                sub={`${stats?.email.failed ?? 0} failed`}
                color={
                  (stats?.email.delivery_rate ?? 0) >= 0.9
                    ? "text-green-600"
                    : "text-amber-600"
                }
                icon={Mail}
              />
              <StatCard
                title="WhatsApp Total"
                value={phoneTotal}
                sub={`${stats?.phone.delivered ?? 0} delivered`}
                color="text-foreground"
                icon={MessageSquare}
              />
              <StatCard
                title="WhatsApp Delivery"
                value={phoneDeliveryRate}
                sub={`${stats?.phone.failed ?? 0} failed`}
                color={
                  (stats?.phone.delivery_rate ?? 0) >= 0.9
                    ? "text-green-600"
                    : "text-amber-600"
                }
                icon={MessageSquare}
              />
            </div>
          )}
        </div>

        {/* Reset daily limit */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-primary" />
              <CardTitle>Reset Daily Resend Limit</CardTitle>
            </div>
            <CardDescription>
              Clear a user's daily OTP resend counter for a channel (admin override).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  User
                </label>
                <UserPicker value={resetUser} onChange={setResetUser} />
              </div>
              <div className="w-full sm:w-40">
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  Channel
                </label>
                <select
                  value={resetChannel}
                  onChange={(e) => setResetChannel(e.target.value as "email" | "phone")}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                </select>
              </div>
              <Button
                onClick={() => setResetConfirmOpen(true)}
                disabled={!resetUser}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset Limit
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Config */}
        <div>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Runtime Configuration
          </h2>

          {configLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-48 w-full rounded-xl" />
              ))}
            </div>
          ) : configError ? (
            <div className="flex items-center gap-2 rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {configError}
            </div>
          ) : config ? (
            <div className="space-y-4">
              {/* Note banner */}
              <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-400">
                <Settings2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{config.note}</span>
              </div>

              {/* Channel settings — two-column grid (Email | WhatsApp) */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <ConfigSection
                  title="Email Settings"
                  description="Rate limits and cooldowns for email OTP delivery"
                  icon={Mail}
                  fields={config.email}
                  edits={edits}
                  onChange={handleFieldChange}
                  className="h-full"
                />

                <ConfigSection
                  title="WhatsApp / Phone Settings"
                  description="Rate limits and cooldowns for WhatsApp OTP delivery"
                  icon={MessageSquare}
                  fields={config.phone}
                  edits={edits}
                  onChange={handleFieldChange}
                  className="h-full"
                />
              </div>

              {/* Global policies — full width with a two-column field grid */}
              <ConfigSection
                title="Global Policies"
                description="Settings that apply to both email and phone channels"
                icon={SlidersHorizontal}
                fields={config.shared}
                edits={edits}
                onChange={handleFieldChange}
                columns={2}
              />

              {/* Save bar */}
              <div className="sticky bottom-0 flex items-center justify-between gap-4 rounded-xl border border-border bg-card/90 px-4 py-3 shadow-lg backdrop-blur-sm">
                <div className="flex items-center gap-2">
                  {dirtyCount > 0 ? (
                    <Badge variant="secondary">
                      {dirtyCount} unsaved change{dirtyCount !== 1 ? "s" : ""}
                    </Badge>
                  ) : saveSuccess ? (
                    <span className="flex items-center gap-1.5 text-sm text-green-600">
                      <CheckCircle2 className="h-4 w-4" />
                      Saved
                    </span>
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      No pending changes
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  {dirtyCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEdits({});
                        setSaveSuccess(false);
                      }}
                    >
                      Discard
                    </Button>
                  )}
                  <Button
                    size="sm"
                    disabled={dirtyCount === 0 || isSaving}
                    onClick={() => void handleSave()}
                  >
                    {isSaving ? (
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Reset confirm dialog */}
      <Dialog
        open={resetConfirmOpen}
        onOpenChange={(open) => {
          if (!open && !isResetting) setResetConfirmOpen(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset daily resend limit?</DialogTitle>
            <DialogDescription>
              This clears the {resetChannel} daily resend counter for{" "}
              <span className="font-medium text-foreground">{resetUser?.email}</span>, allowing
              them to request new OTPs immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResetConfirmOpen(false)}
              disabled={isResetting}
            >
              Cancel
            </Button>
            <Button onClick={() => void handleResetDailyLimit()} disabled={isResetting}>
              {isResetting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting…
                </>
              ) : (
                "Reset Limit"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
